import { type FC } from 'react';
import { Card, CardContent, Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { LocationOn, Add as AddIcon, CheckCircle } from '@mui/icons-material';
import { Site } from '@/types/site.types';

interface SiteFilterCardProps {
    site: Site;
    isSelected: boolean;
    isUploading: boolean;
    isSuperAdmin: boolean;
    onClick: () => void;
    onAddPhotos: (siteName: string) => void;
}

/**
 * Site filter card component for the gallery filter section
 */
const SiteFilterCard: FC<SiteFilterCardProps> = ({
    site,
    isSelected,
    isUploading,
    isSuperAdmin,
    onClick,
    onAddPhotos
}) => {
    return (
        <Card
            onClick={onClick}
            sx={{
                py: 2,
                height: '100%',
                width: '100%',
                cursor: 'pointer',
                position: 'relative',
                border: isSelected ? '2px solid' : '1px solid',
                borderColor: isSelected ? 'primary.main' : '#e5e7eb',
                background: isSelected
                    ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                    : 'white',
                boxShadow: isSelected
                    ? '0 4px 12px rgba(25, 118, 210, 0.2)'
                    : '0 1px 3px rgba(0, 0, 0, 0.08)',
                willChange: 'transform, box-shadow',
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, border-color 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isSelected
                        ? '0 12px 24px rgba(25, 118, 210, 0.3)'
                        : '0 8px 16px rgba(0, 0, 0, 0.12)',
                }
            }}
        >
            <CardContent sx={{ pr: isSuperAdmin ? 5 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography
                        fontWeight={600}
                        sx={{
                            color: isSelected ? 'primary.main' : 'primary.dark',
                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem', lg: '0.95rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                        }}
                    >
                        {site.Name}
                    </Typography>
                    {isSelected && (
                        <CheckCircle
                            sx={{
                                fontSize: { xs: 16, sm: 18, md: 20 },
                                color: 'primary.main',
                                flexShrink: 0
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <LocationOn sx={{
                        fontSize: { xs: 14, sm: 16, md: 18 },
                        color: isSelected ? 'primary.main' : 'text.secondary',
                        flexShrink: 0,
                        mt: 0.2
                    }} />
                    <Typography
                        variant="body2"
                        color={isSelected ? 'primary.main' : 'text.secondary'}
                        sx={{
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.4
                        }}
                    >
                        {site.Location}
                    </Typography>
                </Box>
            </CardContent>
            {isSuperAdmin && (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        onAddPhotos(site.Name);
                    }}
                    disabled={isUploading}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        width: 28,
                        height: 28,
                        backgroundColor: 'primary.dark',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'secondary.main'
                        },
                        '&:disabled': {
                            backgroundColor: '#ccc'
                        }
                    }}
                >
                    {isUploading ? (
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : (
                        <AddIcon fontSize="small" />
                    )}
                </IconButton>
            )}
        </Card>
    );
};

export default SiteFilterCard;
